// Инструмент «Метка»: тап по карте ставит точку, повторный тап по ней снимает.
// Под точкой - нижняя панель (MapPointSheet.vue): координаты, «Своя метка»
// (сохраняется на бэкенде, см. store/myMarkers.ts), «Маршруты сюда»/«Старт
// отсюда» (открывают редактор маршрута у этой точки - см. колбэки в options).
// Свои метки рисуются на карте всегда, пока пользователь залогинен, и тоже
// открывают панель по тапу (переименовать/удалить).
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import { computed, ref, watch } from "vue";
import type { MapCtx } from "../map/mapContext";
import { escapeHtml, markerElement } from "../map/mapUiHelpers";
import { useMyMarkersStore, type MarkerKind, type UserMarker } from "../store/myMarkers";
import { useAuthStore } from "../store/auth";

export interface UsePointToolOptions {
  // Открыть редактор у точки; start=true - сразу поставить её первой вершиной.
  openEditorAt: (lngLat: [number, number], start: boolean) => void;
  onError: (message: string) => void;
}

const DOT_SIZE: [number, number] = [20, 20];

export function usePointTool(ctx: MapCtx, options: UsePointToolOptions) {
  const store = useMyMarkersStore();
  const auth = useAuthStore();

  const active = ref(false);
  const point = ref<{ lng: number; lat: number } | null>(null);
  const selectedMarkerId = ref<number | null>(null);
  const busy = ref(false);

  const selectedMarker = computed<UserMarker | null>(
    () => store.items.find((m) => m.id === selectedMarkerId.value) ?? null
  );

  let droppedMarker: Marker | null = null;
  const ownMarkers = new Map<number, Marker>();

  const removeDroppedMarker = () => {
    droppedMarker?.remove();
    droppedMarker = null;
  };

  const syncDroppedMarker = () => {
    const map = ctx.getMap();
    // Точка сохранённой метки показывается самой меткой, не второй точкой.
    if (!map || !point.value || selectedMarkerId.value != null) {
      removeDroppedMarker();
      return;
    }
    if (!droppedMarker) {
      const el = markerElement(
        "point-tool-marker",
        `<div class="point-tool-dot" aria-label="${escapeHtml(ctx.t("map.pointTool.title"))}"></div>`,
        DOT_SIZE
      );
      // Тап по самой точке снимает её; stopPropagation - иначе клик
      // всплывёт в map "click" и тут же поставит точку заново.
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        close();
      });
      droppedMarker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([point.value.lng, point.value.lat])
        .addTo(map);
      return;
    }
    droppedMarker.setLngLat([point.value.lng, point.value.lat]);
  };

  const syncOwnMarkers = () => {
    const map = ctx.getMap();
    const wanted = new Map(auth.isAuthenticated ? store.items.filter((m) => m.visible !== false).map((m) => [m.id, m]) : []);
    for (const [id, marker] of ownMarkers) {
      if (!wanted.has(id)) {
        marker.remove();
        ownMarkers.delete(id);
      }
    }
    if (!map) return;
    for (const [id, m] of wanted) {
      const html = `<div class="own-marker"><span class="own-marker__dot"></span><span class="own-marker__label">${escapeHtml(m.name)}</span></div>`;
      const existing = ownMarkers.get(id);
      if (existing) {
        existing.setLngLat([m.lng, m.lat]);
        existing.getElement().innerHTML = html;
        continue;
      }
      const el = markerElement("own-marker-icon", html);
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        select(m);
      });
      ownMarkers.set(
        id,
        new maplibregl.Marker({ element: el, anchor: "top-left" }).setLngLat([m.lng, m.lat]).addTo(map)
      );
    }
  };

  const select = (m: UserMarker) => {
    selectedMarkerId.value = m.id;
    point.value = { lng: m.lng, lat: m.lat };
    syncDroppedMarker();
  };

  const close = () => {
    point.value = null;
    selectedMarkerId.value = null;
    syncDroppedMarker();
  };

  const onMapClick = (e: maplibregl.MapMouseEvent) => {
    if (!active.value) return;
    selectedMarkerId.value = null;
    point.value = { lng: e.lngLat.lng, lat: e.lngLat.lat };
    syncDroppedMarker();
  };

  const activate = () => {
    active.value = true;
    const map = ctx.getMap();
    if (map) map.getCanvas().style.cursor = "crosshair";
  };

  const deactivate = () => {
    active.value = false;
    close();
    const map = ctx.getMap();
    if (map) map.getCanvas().style.cursor = "";
  };

  const toggle = () => (active.value ? deactivate() : activate());

  const save = async (payload: { name: string; kind: MarkerKind }) => {
    if (!point.value || busy.value) return;
    busy.value = true;
    try {
      if (selectedMarker.value) {
        if (!(await store.update(selectedMarker.value.id, payload))) options.onError(ctx.t("map.pointTool.failed"));
        return;
      }
      const created = await store.create({ ...payload, lat: point.value.lat, lng: point.value.lng });
      if (!created) {
        options.onError(ctx.t("map.pointTool.failed"));
        return;
      }
      selectedMarkerId.value = created.id;
      syncDroppedMarker();
    } finally {
      busy.value = false;
    }
  };

  const removeSelected = async () => {
    if (!selectedMarker.value || busy.value) return;
    busy.value = true;
    try {
      if (await store.remove(selectedMarker.value.id)) close();
      else options.onError(ctx.t("map.pointTool.failed"));
    } finally {
      busy.value = false;
    }
  };

  const routesHere = () => {
    if (point.value) options.openEditorAt([point.value.lng, point.value.lat], false);
  };
  const startHere = () => {
    if (point.value) options.openEditorAt([point.value.lng, point.value.lat], true);
  };

  // Личные метки: грузим при входе, забываем при выходе (метки прошлого
  // пользователя не должны остаться ни в памяти, ни на карте).
  watch(
    () => auth.isAuthenticated,
    (authed) => {
      if (authed) void store.fetch();
      else store.clear();
      syncOwnMarkers();
    },
    { immediate: true }
  );
  watch(() => store.items, syncOwnMarkers, { deep: true });

  // Карта пересоздаётся (потеря WebGL-контекста) - DOM-маркеры надо
  // навесить заново на новый экземпляр.
  const resyncAll = () => {
    for (const marker of ownMarkers.values()) marker.remove();
    ownMarkers.clear();
    removeDroppedMarker();
    syncOwnMarkers();
    syncDroppedMarker();
  };

  const destroy = () => {
    for (const marker of ownMarkers.values()) marker.remove();
    ownMarkers.clear();
    removeDroppedMarker();
  };

  return {
    active,
    point,
    selectedMarker,
    busy,
    toggle,
    activate,
    deactivate,
    close,
    onMapClick,
    save,
    removeSelected,
    selectMarker: select,
    routesHere,
    startHere,
    resyncAll,
    destroy,
  };
}
